import React from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { gql, useMutation } from "urql"
import * as Yup from "yup"

import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/solid"

import { Color } from "~tailwindui/types/enums"

import { Button } from "~tailwindui/Basics"
import {
  AutocompleteSelect,
  Failures,
  SubmitButton,
  TextInput,
  handleFailure,
} from "~tailwindui/Form"
import Modal from "~tailwindui/Modal"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

export type NewThreadProps = {
  updateThreads: any
}

const ValidationSchema = Yup.object().shape({
  userIds: Yup.array().required("You must select at least one recipient"),
  title: Yup.string().required("Please specify a title for this thread"),
})

const NewThread: React.FC<NewThreadProps> = ({ updateThreads }) => {
  const [, runMutation]: any[] = useMutation(createThreadMutation)
  const formState = {
    userIds: [],
    title: "",
  }

  const handleSubmit = (values, actions, closeModal) => {
    runMutation(values)
      .then(
        result => {
          const { failures, messageThread } = result.data.createMessageThread
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info("Thread created")
            updateThreads(messageThread)
            closeModal()
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <Modal.Dialog
      buttonText="Create Thread"
      buttonClassNames={buttonClassNames({ color: Color.Teal })}
    >
      {closeModal => (
        <Formik
          initialValues={formState}
          onSubmit={(values, actions) =>
            handleSubmit(values, actions, closeModal)
          }
          validationSchema={ValidationSchema}
          validateOnBlur={false}
        >
          <Form>
            <Modal.Header
              Icon={ChatBubbleLeftRightIcon}
              iconClassNames="text-blue-600"
            >
              Create New Thread
            </Modal.Header>
            <Modal.Body>
              <TextInput
                name="title"
                label="Thread Title (this will be viewable by all parties)"
                placeholder="ex, 6th Grade Math"
              />

              <AutocompleteSelect
                clearable
                label="Participants"
                multi
                name="userIds"
                resource="messageableUsers"
                searchColumn="fullName"
                portal
              />
            </Modal.Body>
            <Modal.Footer>
              <div className="flex w-full flex-col space-y-4">
                <Failures />
                <div className="flex w-full justify-between">
                  <Button onClick={closeModal}>Cancel</Button>
                  <SubmitButton text="Create Thread" color={Color.Teal} />
                </div>
              </div>
            </Modal.Footer>
          </Form>
        </Formik>
      )}
    </Modal.Dialog>
  )
}

const createThreadMutation = gql`
  mutation CreateMessageThread($userIds: [ID!]!, $title: String!) {
    createMessageThread(userIds: $userIds, title: $title) {
      failures {
        message
      }
      messageThread {
        id
        title
        messagesCount
        unreadMessagesCount
        createdAt
        users {
          id
          type
          firstName
          messagingDisplayName
          avatar {
            thumbUrl
          }
        }
        messages {
          totalPages
          currentPage
          data {
            id
            read
            content
            createdAt
            contentFlagged
            author {
              id
              type
              firstName
              messagingDisplayName
              avatar {
                thumbUrl
              }
            }
            attachment {
              filename
              contentType
              downloadUrl
              url
              thumbUrl
              previewUrl
            }
          }
        }
        author {
          id
          type
          firstName
          messagingDisplayName
          avatar {
            thumbUrl
          }
        }
      }
    }
  }
`

export default NewThread
